import React, { useState } from 'react';

// Read-only view over orders/{id}/verification, nepp, coldChainConfirmed —
// all written server-side by /pharmacy/verify-prescription, never by a
// client — so this tab is just surfacing what already happened, not
// managing any state of its own. A pom/controlled order that reached
// "dispatched" or later without order.verification means the backend gate
// (assignNearestRider) was somehow bypassed — that should be architecturally
// impossible today, so a flagged row here is worth investigating immediately.
const DISPATCH_STAGE = ['dispatched', 'delivering', 'delivered'];

export default function ComplianceTab({ orders }) {
  const [search, setSearch] = useState('');

  const complianceOrders = (orders || [])
    .filter(o => o.drugSchedule && o.drugSchedule !== 'otc')
    .filter(o => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (o.customerName || '').toLowerCase().includes(q) ||
        (o.orderId || o.id).toLowerCase().includes(q) ||
        (o.verification?.pharmacistName || '').toLowerCase().includes(q);
    })
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const bypassed = complianceOrders.filter(o => DISPATCH_STAGE.includes(o.status) && !o.verification);

  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ fontWeight: 700, margin: '0 0 4px', fontSize: 20 }}>🔒 Compliance</h2>
      <p style={{ color: '#888', fontSize: 13, margin: '0 0 20px' }}>
        Every prescription/controlled order's verification, pharmacist, and NEPP submission — nothing here is editable, it's all written by the backend at the moment a pharmacist verifies an order.
      </p>

      {bypassed.length > 0 && (
        <div style={{ backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <p style={{ fontWeight: 700, color: '#B91C1C', margin: '0 0 6px', fontSize: 14 }}>
            🚨 {bypassed.length} order(s) reached dispatch without a recorded verification
          </p>
          <p style={{ fontSize: 12, color: '#B91C1C', margin: 0 }}>
            This should be blocked automatically — investigate immediately: {bypassed.map(o => `#${(o.orderId || o.id).slice(-6).toUpperCase()}`).join(', ')}
          </p>
        </div>
      )}

      <input
        style={{ width: '100%', maxWidth: 340, padding: '9px 14px', borderRadius: 10, border: '1px solid #E5E7EB', fontSize: 13, marginBottom: 16, boxSizing: 'border-box' }}
        placeholder="🔍 Search order, customer or pharmacist…"
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      <div style={{ backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ backgroundColor: '#F7F8FA', textAlign: 'left' }}>
              {['Order', 'Schedule', 'Status', 'Verified At', 'Pharmacist', 'NEPP ID', 'Cold Chain'].map(h => (
                <th key={h} style={{ padding: '10px 14px', fontWeight: 700, color: '#555', borderBottom: '1px solid #E5E7EB' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {complianceOrders.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: '#888' }}>No prescription/controlled orders yet.</td></tr>
            ) : complianceOrders.map(o => (
              <tr key={o.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                <td style={{ padding: '10px 14px' }}>#{(o.orderId || o.id).slice(-6).toUpperCase()}</td>
                <td style={{ padding: '10px 14px' }}>
                  {o.drugSchedule === 'controlled'
                    ? <span style={{ backgroundColor: '#1A1A1A', color: '#FFF', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>⚫ Controlled</span>
                    : <span style={{ backgroundColor: '#FEE2E2', color: '#B91C1C', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>🔴 POM</span>}
                </td>
                <td style={{ padding: '10px 14px' }}>{o.status}</td>
                <td style={{ padding: '10px 14px', color: '#888' }}>{o.verification?.verifiedAt ? new Date(o.verification.verifiedAt).toLocaleString() : '—'}</td>
                <td style={{ padding: '10px 14px' }}>{o.verification ? `${o.verification.pharmacistName} (${o.verification.pharmacistRegNo})` : <span style={{ color: '#B91C1C' }}>Not verified</span>}</td>
                <td style={{ padding: '10px 14px' }}>{o.nepp?.transactionId ? `${o.nepp.transactionId}${o.nepp.stub ? ' (stub)' : ''}` : '—'}</td>
                <td style={{ padding: '10px 14px' }}>
                  {o.items?.some(i => i.coldChain) ? (o.coldChainConfirmed ? '✅' : <span style={{ color: '#B91C1C' }}>❌ Not confirmed</span>) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
